import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    addUpdateGeojsonLayers,
    removeSourcesIfExists,
    removeLayersIfExists,
    addUpdateGeojsonSource,
} from "@/lib/utils/map";
import useMap from "@/lib/map/useMap";
import { roundTo } from "@/lib/utils/numbers";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { ImageAnalytics } from "@/types/imagery";
import {
    getKeyValue,
    SortDescriptor,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FillLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import { LineLayerSpecification } from "mapbox-gl";
import { useFormContext } from "react-hook-form";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";

const TABLE_SOURCE = "image-analytics-table-source";
const FILL_LAYER: FillLayerSpecification = {
    source: TABLE_SOURCE,
    id: "image-analytics-table-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.8,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: TABLE_SOURCE,
    id: "image-analytics-table-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": "#FFF",
        "line-width": 3,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: TABLE_SOURCE,
    id: "image-analytics-table-symbol",
    type: "symbol",
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
    },
};

function SIBarTable({
    imageAnalyticsQuery,
}: {
    imageAnalyticsQuery: UseQueryResult<ImageAnalytics>;
}) {
    const { t } = useTranslation("insights");
    const { fieldQuery } = useFieldDashboard() ?? {};
    const { isGrower } = useAuth();

    const f = useFormContext();
    const pairIds = f.watch("pairIds");

    const plots = useMemo(() => {
        return (
            imageAnalyticsQuery?.data?.features
                .map((plot, index) => ({
                    ...plot.properties,
                    index,
                    sensitized_si: plot.properties.sensitized_si
                        ? roundTo(plot.properties.sensitized_si * 100, 1)
                        : null,
                    ndre_mean: roundTo(plot.properties.ndre_mean, 4),
                    area_rep: roundTo(plot.properties.area_rep, 1),
                    percent_area_rep: roundTo(
                        plot.properties.percent_area_rep,
                        1,
                    ),
                    id: plot.id,
                }))
                .filter((plot) => {
                    if (!pairIds?.length) return plot;
                    return pairIds.includes(plot.pair_id?.toString() ?? "asdf");
                })
                .filter((plot) => {
                    return !isGrower || plot.plot_type !== "Bulk";
                }) ?? []
        );
    }, [imageAnalyticsQuery?.data, pairIds, isGrower]);

    const bands = useMemo(
        () =>
            calculateYBands(
                fieldQuery?.data?.insight_sensitivity ?? "moderate",
            ),
        [fieldQuery?.data?.insight_sensitivity],
    );

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, TABLE_SOURCE, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });

        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map && mapLoaded) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [TABLE_SOURCE]);
            }
        };
    }, [map, mapLoaded]);

    const onMouseEnter = useCallback(
        (featureId: number) => {
            if (!map || !mapLoaded) return;
            const feature = imageAnalyticsQuery.data?.features.find(
                (f) => f.id === featureId,
            );
            if (!feature) return;
            const color =
                bands.find((band) => {
                    return (
                        feature.properties?.sensitized_si != null &&
                        feature.properties.sensitized_si * 100 >= band.y0 &&
                        feature.properties.sensitized_si * 100 < band.y1
                    );
                })?.mapColor || "gray";
            addUpdateGeojsonSource(map, TABLE_SOURCE, {
                type: "geojson",
                data: {
                    ...feature,
                    properties: { ...feature.properties, color },
                },
                tolerance: 0,
            });
        },
        [map, mapLoaded, imageAnalyticsQuery.data?.features, bands],
    );

    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "date",
        direction: "ascending",
    });
    const sortedItems = useMemo(() => {
        const { column, direction } = sortDescriptor;
        if (!column) return plots;
        const sorted = sortArrayByKey(
            plots,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            column as any,
            direction === "descending" ? "desc" : "asc",
        );
        return sorted;
    }, [plots, sortDescriptor]);

    const COLUMNS = useMemo(
        () => [
            { key: "pair_id", label: t("tableColumns.pairId") },
            { key: "plot_type", label: t("tableColumns.plotType") },
            { key: "sensitized_si", label: t("tableColumns.siPercent") },
            { key: "ndre_mean", label: t("tableColumns.ndre") },
            { key: "zone_number", label: t("tableColumns.zone") },
            { key: "area_rep", label: t("tableColumns.rangeAc") },
            { key: "percent_area_rep", label: t("tableColumns.weightPercent") },
        ],
        [t],
    );

    return (
        <Table
            isHeaderSticky
            isVirtualized
            maxTableHeight={500}
            aria-label={t("ariaLabels.trendline")}
            onSortChange={setSortDescriptor}
            sortDescriptor={sortDescriptor}
            classNames={{
                th: "px-[0.25rem]",
                td: "px-[0.25rem]",
                wrapper: "p-0 shadow-none rounded-none",
            }}
        >
            <TableHeader columns={COLUMNS}>
                {(column) => (
                    <TableColumn key={column.key} allowsSorting>
                        {column.label}
                    </TableColumn>
                )}
            </TableHeader>
            <TableBody
                items={sortedItems}
                emptyContent={t("messages.noDataDateRange")}
                isLoading={imageAnalyticsQuery.isFetching}
                loadingContent={<Spinner label={t("messages.loading")} />}
            >
                {(item) => (
                    <TableRow
                        key={item.index}
                        className="hover:bg-gray-100 rounded-lg"
                        onMouseEnter={() => {
                            onMouseEnter(item.id as number);
                        }}
                    >
                        {(columnKey) => {
                            if (columnKey === "sensitized_si") {
                                const si = getKeyValue(item, columnKey);
                                const color =
                                    bands.find(
                                        (band) => si > band.y0 && si <= band.y1,
                                    )?.color ?? "white";
                                return (
                                    <TableCell>
                                        <div
                                            style={{
                                                backgroundColor: color,
                                            }}
                                            className="rounded-md font-medium  p-1 m-0 text-center justify-self-start"
                                        >
                                            {si}%
                                        </div>
                                    </TableCell>
                                );
                            }
                            return (
                                <TableCell className="text-xs">
                                    {getKeyValue(item, columnKey)}
                                </TableCell>
                            );
                        }}
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
}

export default SIBarTable;
